import { dataNodeKindsJson, nodeDefaultsJson } from 'fluveria-engine-wasm';
import { getOutputNodeInputType, type NodeDefBase, type NodeKind } from './nodeDefs.js';
import { RENDER_NODE_DEFS } from './renderNodeDefs.js';
import { RUNTIME_VALUE_TYPE_OPTIONS, runtimeValuePortType } from './runtimeNodeTypes.js';

type DataNodeDef = NodeDefBase & { defaults?: Record<string, unknown>; graphs: ['data'] };

const sourceDefs: Record<string, DataNodeDef> = {
  Time: {
    label: 'Time', cat: 'source', inputs: [], graphs: ['data'],
    outputs: [{ name: 'elapsed', label: 'Elapsed', type: 'float' }, { name: 'delta', label: 'Delta', type: 'float' }],
  },
  Pointer: {
    label: 'Pointer', cat: 'source', inputs: [], graphs: ['data'],
    outputs: [
      { name: 'local', label: 'Local', type: 'vec2' },
      { name: 'normalized', label: 'Normalized', type: 'vec2' },
      { name: 'down', label: 'Down', type: 'float' },
      { name: 'present', label: 'Present', type: 'float' },
    ],
  },
  Hover: {
    label: 'Hover', cat: 'source', inputs: [], graphs: ['data'],
    outputs: [{ name: 'value', label: 'Value', type: 'float' }],
  },
  Pressed: {
    label: 'Pressed', cat: 'source', inputs: [], graphs: ['data'],
    outputs: [{ name: 'value', label: 'Value', type: 'float' }],
  },
  // A press that went down outside the node the graph runs on.
  PressOutside: {
    label: 'Press Outside', cat: 'source', inputs: [], graphs: ['data'],
    outputs: [{ name: 'value', label: 'Value', type: 'float' }],
  },
  // Along the node's axis: where the port is looking, how much it shows,
  // how long the content is.
  Scroll: {
    label: 'Scroll', cat: 'source', inputs: [], graphs: ['data'],
    outputs: [
      { name: 'offset', label: 'Offset', type: 'float' },
      { name: 'viewport', label: 'Viewport', type: 'float' },
      { name: 'content', label: 'Content', type: 'float' },
    ],
    ui: [
      { kind: 'select', field: 'axis', label: 'Axis', default: 'y', options: [
        { value: 'x', label: 'X' }, { value: 'y', label: 'Y' },
      ] },
      { kind: 'port', ref: 'offset' },
      { kind: 'port', ref: 'viewport' },
      { kind: 'port', ref: 'content' },
    ],
  },
  // How much of a folding frame is shown, nought to whole.
  Reveal: {
    label: 'Reveal', cat: 'source', inputs: [], graphs: ['data'],
    outputs: [{ name: 'shown', label: 'Shown', type: 'float' }],
  },
  State: {
    label: 'State', cat: 'source', inputs: [], graphs: ['data'],
    defaults: { name: '', scope: 'global', valueType: 'number' },
    outputs: node => [{
      name: 'value',
      label: 'Value',
      type: runtimeValuePortType(node.data.valueType),
    }],
    ui: [
      { kind: 'text', field: 'name', label: 'State', placeholder: 'State name' },
      { kind: 'port', ref: 'value', io: 'out' },
      { kind: 'select', field: 'scope', label: 'Scope', default: 'global', options: [
        { value: 'global', label: 'Global' }, { value: 'owner', label: 'Owner' },
      ] },
      { kind: 'select', field: 'valueType', label: 'Type', default: 'number', options: RUNTIME_VALUE_TYPE_OPTIONS },
    ],
  },
  Input: {
    label: 'Input', cat: 'source', inputs: [], graphs: ['data'],
    defaults: { name: 'input', valueType: 'number', default: 0 },
    outputs: node => [{
      name: 'value',
      label: 'Value',
      type: runtimeValuePortType(node.data.valueType),
    }],
    ui: [
      { kind: 'text', field: 'name', label: 'Input', placeholder: 'input name' },
      { kind: 'select', field: 'valueType', label: 'Type', default: 'number', options: RUNTIME_VALUE_TYPE_OPTIONS },
      { kind: 'text', field: 'default', placeholder: 'Default', valueTypeField: 'valueType' },
      { kind: 'port', ref: 'value', io: 'out' },
    ],
  },
  Lookup: {
    label: 'Lookup', cat: 'source', graphs: ['data'],
    defaults: { source: '', field: '', keyType: 'number', valueType: 'string' },
    inputs: node => [{
      name: 'key', label: 'Key',
      type: node.data.keyType === 'number' ? 'float' : node.data.keyType as 'bool' | 'string',
    }],
    outputs: node => [{ name: 'value', label: 'Value', type: runtimeValuePortType(node.data.valueType) }],
    ui: [
      { kind: 'port', ref: 'key', io: 'in' },
      { kind: 'data-source', field: 'source', label: 'Source' },
      { kind: 'data-field', field: 'field', sourceField: 'source', label: 'Field' },
      { kind: 'port', ref: 'value', io: 'out' },
    ],
  },
  Query: {
    label: 'Query', cat: 'source', inputs: [], graphs: ['data'],
    defaults: { query: '', output: '', valueType: 'number' },
    outputs: node => [{ name: 'value', label: 'Value', type: runtimeValuePortType(node.data.valueType) }],
    ui: [
      { kind: 'query-graph', field: 'query', label: 'Query' },
      { kind: 'query-output', field: 'output', queryField: 'query', label: 'Output' },
      { kind: 'select', field: 'valueType', label: 'Type', default: 'number', options: RUNTIME_VALUE_TYPE_OPTIONS },
      { kind: 'port', ref: 'value', io: 'out' },
    ],
  },
  RecordField: {
    label: 'Record Field', cat: 'source', inputs: [], graphs: ['data'],
    defaults: { field: '', valueType: 'string' },
    outputs: node => [{ name: 'value', label: 'Value', type: runtimeValuePortType(node.data.valueType) }],
    ui: [
      { kind: 'record-field', field: 'field', label: 'Field' },
      { kind: 'select', field: 'valueType', label: 'Type', default: 'string', options: RUNTIME_VALUE_TYPE_OPTIONS },
      { kind: 'port', ref: 'value', io: 'out' },
    ],
  },
  Equal: {
    label: 'Equal', cat: 'logic', inputs: [
      { name: 'a', label: 'A', type: 'float' },
      { name: 'b', label: 'B', type: 'float' },
    ], outputs: [{ name: 'value', label: 'Value', type: 'bool' }], graphs: ['data'],
  },
  // The comparisons and logic of a data graph, under the names and port
  // order the event graph gives them (nodes.rs): `a` and `b`, a truth out.
  ...Object.fromEntries((['Greater', 'GreaterEqual', 'Less', 'LessEqual'] as const).map(kind => [kind, {
    label: kind.replace('Equal', ' Equal'), cat: 'logic', graphs: ['data'],
    inputs: [{ name: 'a', label: 'A', type: 'float' }, { name: 'b', label: 'B', type: 'float' }],
    outputs: [{ name: 'value', label: 'Value', type: 'bool' }],
  } satisfies DataNodeDef])),
  ...Object.fromEntries((['And', 'Or'] as const).map(kind => [kind, {
    label: kind, cat: 'logic', graphs: ['data'],
    inputs: [{ name: 'a', label: 'A', type: 'bool' }, { name: 'b', label: 'B', type: 'bool' }],
    outputs: [{ name: 'value', label: 'Value', type: 'bool' }],
  } satisfies DataNodeDef])),
  Not: {
    label: 'Not', cat: 'logic', graphs: ['data'],
    inputs: [{ name: 'value', label: 'Value', type: 'bool' }],
    outputs: [{ name: 'value', label: 'Value', type: 'bool' }],
  },
  BoolToNumber: {
    label: 'Bool to Number', cat: 'logic', inputs: [{ name: 'value', label: 'Value', type: 'bool' }],
    outputs: [{ name: 'value', label: 'Value', type: 'float' }], graphs: ['data'],
  },
  // A time in seconds as a clock writes it (crates/graph/src/data.rs
  // format_duration). The pattern is read from the wired text, else the field.
  FormatDuration: {
    label: 'Format Duration', cat: 'logic', graphs: ['data'],
    inputs: [
      { name: 'value', label: 'Seconds', type: 'float' },
      { name: 'pattern', label: 'Pattern', type: 'string', optional: true },
    ],
    outputs: [{ name: 'text', label: 'Text', type: 'string' }],
    ui: [
      { kind: 'port', ref: 'value' },
      { kind: 'port', ref: 'pattern' },
      { kind: 'text', field: 'pattern', placeholder: 'mm:ss' },
      { kind: 'port', ref: 'text' },
    ],
  },
  // Text out of a data graph (crates/graph/src/data.rs). A text input left
  // unwired reads the node's field of its name, as an unwired number does.
  // `digits`: as many digits at least before the point, zeros in front.
  FormatNumber: {
    label: 'Format Number', cat: 'logic', graphs: ['data'],
    inputs: [
      { name: 'value', label: 'Value', type: 'float' },
      { name: 'decimals', label: 'Decimals', type: 'float', range: { min: 0, max: 12, step: 1 } },
      { name: 'unit', label: 'Unit', type: 'string', optional: true },
      { name: 'digits', label: 'Digits', type: 'float', optional: true },
    ],
    outputs: [{ name: 'text', label: 'Text', type: 'string' }],
    ui: [
      { kind: 'port', ref: 'value' },
      { kind: 'port', ref: 'decimals' },
      { kind: 'port', ref: 'unit' },
      { kind: 'text', field: 'unit', placeholder: 'unit' },
      { kind: 'port', ref: 'digits' },
      { kind: 'port', ref: 'text' },
    ],
  },
  Concat: {
    label: 'Concat', cat: 'logic', graphs: ['data'],
    inputs: [
      { name: 'a', label: 'A', type: 'string', optional: true },
      { name: 'b', label: 'B', type: 'string', optional: true },
    ],
    outputs: [{ name: 'text', label: 'Text', type: 'string' }],
  },
  // A list of texts: how many, the one at an index, all of them joined.
  ListLength: {
    label: 'List Length', cat: 'logic', graphs: ['data'],
    inputs: [{ name: 'list', label: 'List', type: 'strings', optional: true }],
    outputs: [{ name: 'length', label: 'Length', type: 'float' }],
  },
  PickText: {
    label: 'Pick Text', cat: 'logic', graphs: ['data'],
    inputs: [
      { name: 'list', label: 'List', type: 'strings', optional: true },
      { name: 'index', label: 'Index', type: 'float', range: { min: 0, max: Number.POSITIVE_INFINITY, step: 1 } },
    ],
    outputs: [{ name: 'text', label: 'Text', type: 'string' }],
  },
  JoinText: {
    label: 'Join Text', cat: 'logic', graphs: ['data'],
    inputs: [
      { name: 'list', label: 'List', type: 'strings', optional: true },
      { name: 'separator', label: 'Separator', type: 'string', optional: true },
    ],
    outputs: [{ name: 'text', label: 'Text', type: 'string' }],
    ui: [
      { kind: 'port', ref: 'list' },
      { kind: 'port', ref: 'separator' },
      { kind: 'text', field: 'separator', placeholder: 'separator' },
      { kind: 'port', ref: 'text' },
    ],
  },
  // A string typed into by the keys the node holds the keyboard for
  // (crates/graph/src/typing.rs), laid out as a `Text` in a box the size it
  // is given; its outputs are the text, the caret and selection as bytes,
  // focus, Enter, the caret's blink and place, and the scroll.
  TextEdit: {
    label: 'Text Edit', cat: 'source', graphs: ['data'],
    inputs: [
      { name: 'value', label: 'Value', type: 'string', optional: true },
      { name: 'size', label: 'Font size', type: 'float' },
      { name: 'press', label: 'Press', type: 'float' },
      { name: 'pointerX', label: 'Pointer X', type: 'float' },
      { name: 'pointerY', label: 'Pointer Y', type: 'float' },
      { name: 'width', label: 'Width', type: 'float' },
      { name: 'height', label: 'Height', type: 'float' },
      { name: 'font', label: 'Font', type: 'string', optional: true },
      { name: 'scrollX', label: 'Scroll X', type: 'float' },
      { name: 'scrollY', label: 'Scroll Y', type: 'float' },
    ],
    outputs: [
      { name: 'text', label: 'Text', type: 'string' },
      { name: 'caret', label: 'Caret', type: 'float' },
      { name: 'selectionStart', label: 'Selection start', type: 'float' },
      { name: 'selectionEnd', label: 'Selection end', type: 'float' },
      { name: 'focused', label: 'Focused', type: 'float' },
      { name: 'submitted', label: 'Submitted', type: 'float' },
      { name: 'caretShown', label: 'Caret shown', type: 'float' },
      { name: 'caretX', label: 'Caret X', type: 'float' },
      { name: 'caretY', label: 'Caret Y', type: 'float' },
      { name: 'scrollX', label: 'Scroll X', type: 'float' },
      { name: 'scrollY', label: 'Scroll Y', type: 'float' },
    ],
  },
  Spring: {
    label: 'Spring', cat: 'math', subcat: 'range', graphs: ['data'],
    inputs: [
      { name: 'target', label: 'Target', type: 'float' },
      { name: 'frequencyHz', label: 'Frequency', type: 'float' },
      { name: 'dampingRatio', label: 'Damping', type: 'float' },
    ],
    outputs: [
      { name: 'value', label: 'Value', type: 'float' },
      { name: 'velocity', label: 'Velocity', type: 'float' },
    ],
  },
  // What `value` was when `hold` turned on, for as long as it stays on.
  Hold: {
    label: 'Hold', cat: 'math', subcat: 'range', graphs: ['data'],
    inputs: [{ name: 'value', label: 'Value', type: 'float' }, { name: 'hold', label: 'Hold', type: 'float' }],
    outputs: [{ name: 'value', label: 'Value', type: 'float' }],
  },
  // What `value` was on the tick before this one.
  Previous: {
    label: 'Previous', cat: 'math', subcat: 'range', graphs: ['data'],
    inputs: [{ name: 'value', label: 'Value', type: 'float' }],
    outputs: [{ name: 'value', label: 'Value', type: 'float' }],
  },
  // How long `run` has been on.
  Stopwatch: {
    label: 'Stopwatch', cat: 'math', subcat: 'range', graphs: ['data'],
    inputs: [{ name: 'run', label: 'Run', type: 'float' }],
    outputs: [{ name: 'value', label: 'Value', type: 'float' }],
  },
  Output: {
    label: 'Output', cat: 'output', graphs: ['data'],
    defaults: { inputName: 'Output', inputType: 'float', displayType: 'float' },
    inputs: node => [{
      name: 'value',
      label: String(node.data.inputName || 'Output'),
      type: getOutputNodeInputType(node),
    }],
    outputs: [],
    ui: [
      { kind: 'text', field: 'inputName', placeholder: 'Output name' },
      { kind: 'select', field: 'inputType', label: 'Type', default: 'float', options: [
        { value: 'float', label: 'Float' },
        { value: 'vec2', label: 'Vector 2' },
        { value: 'vec3', label: 'Vector 3' },
        { value: 'vec4', label: 'Vector 4' },
        { value: 'bool', label: 'Boolean' },
        { value: 'string', label: 'String' },
      ] },
      { kind: 'port', ref: 'value' },
    ],
  },
};

let cached: Record<string, DataNodeDef> | null = null;

export function getDataNodeDefs(): Record<string, DataNodeDef> {
  if (cached) return cached;
  const defaults = JSON.parse(nodeDefaultsJson()) as Record<string, Record<string, unknown>>;
  const engineKinds = JSON.parse(dataNodeKindsJson()) as NodeKind[];
  cached = Object.fromEntries(
    engineKinds.flatMap(kind => {
      const base = sourceDefs[kind] ?? RENDER_NODE_DEFS[kind];
      if (!base) return [];
      return [[kind, { ...base, defaults: base.defaults ?? defaults[kind] ?? {}, graphs: ['data'] } as DataNodeDef]];
    }),
  );
  return cached;
}
